/**
 * 關鍵字頁面模組
 */

//-----------------------------------------------------------------------
// 載入模組
import * as React from 'react';
import { JSX } from 'react';
import get from 'lodash/get';
import { useRecordContext, usePermissions, Pagination, ArrayField, SingleFieldList, ChipField, ArrayInput, SimpleFormIterator, Labeled, ReferenceArrayField, ReferenceArrayInput, AutocompleteArrayInput } from 'react-admin';
import { Create, Edit, List, Show, SimpleForm, TabbedShowLayout, Tab, Datagrid, ReferenceManyField } from 'react-admin';
import { NumberField, NumberInput, TextInput, SelectInput, TextField, DateField, ShowButton, ReferenceField } from 'react-admin';
import { downloadCSV, DataProvider } from 'react-admin';
import { SaveOnlyToolbar, ListEditButton, MemberLogTypeField, MemberLogDataField, TimeRangeInput } from '../lib/components';
import jsonExport from 'jsonexport/dist';

//-----------------------------------------------------------------------
// defines
import { COMMON_STATUS, KEYWORD_SEARCH_MODE, MEMBER_GROUP, MEMBER_LOG_TYPE } from '../common/defines';
import { NamePairInfo } from '../common/interfaces';
import { MemberAuthInfo } from '../providers/authProvider';

//-----------------------------------------------------------------------
/** 關鍵字狀態顯示欄位元件。 */

const keywordStatusMap = new Map<string, string>();
const keywordStatusList : NamePairInfo<string>[] =
[
	{ id: COMMON_STATUS.Enable,		name: "啟用"},
	{ id: COMMON_STATUS.Disable,	name: "停用"}
];

for(let status of keywordStatusList)
	keywordStatusMap.set(status.id, status.name);

export const KeywordStatusField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{keywordStatusMap.get(get(record, source))}</span>);
}

KeywordStatusField.defaultProps =
{
	label: '狀態',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 關鍵字搜尋模式顯示欄位元件。 */

const keywordSearchModeMap = new Map<string, string>();
const keywordSearchModeList : NamePairInfo<string>[] =
[
	{ id: KEYWORD_SEARCH_MODE.Default,		name: "來源與頻道"},
	{ id: KEYWORD_SEARCH_MODE.SourceOnly,	name: "只搜尋來源"}
];

for(let mode of keywordSearchModeList)
	keywordSearchModeMap.set(mode.id, mode.name);

export const KeywordSearchModeField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{keywordSearchModeMap.get(get(record, source))}</span>);
}

KeywordSearchModeField.defaultProps =
{
	label: '搜尋模式',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 關鍵字列表過濾器。 */
const keywordFilters =
[
	<TimeRangeInput key='input-time-range' alwaysOn />
];

/** 資料輸出功能。 */
const exporter = (records: any, fetchRelatedRecords: (data: any, field: string, resource: string) => Promise<any>, dataProvider: DataProvider) =>
{
	fetchRelatedRecords(records, 'regions', 'regionAll').then((regions) =>
	{
		const data = records.map((record: any) =>
		{
			let	regionList = record.regions as number[];
			let nameList : string[] = [];

			for(let regionId of regionList)
			{
				if(regions[regionId])
					nameList.push(regions[regionId].name);
			}

			return { ...record, regions: nameList };
		});

		return jsonExport(data,
		{
			headers	: [ 'id', 'text', 'aliases.text', 'regions', 'limit', 'targetCount', 'targetIncrease', 'viewCount', 'viewIncrease', 'viewOfNew', 'commentCount', 'commentIncrease', 'commentOfNew', 'status', 'createTime' ],
			rename	: [ 'id', '內容', '別名', '有效區域', '搜尋量上限', '目標數量', '新增目標量', '累計瀏覽量', '瀏覽增加量', '新增目標瀏覽量', '留言數量', '留言增加量', '新增目標留言量', '狀態', '建立時間' ]
		}, (err, csv) =>
		{
			downloadCSV("\ufeff" + csv, 'keyword');
		});
	});
};

/** 關鍵字列表頁面。 */
export const KeywordList = (props: any) =>
{
	let { permissions } = usePermissions();
	let	authInfo = permissions as MemberAuthInfo;

	return <List {...props} title='關鍵字' filters={keywordFilters} exporter={exporter}>
		<Datagrid bulkActionButtons={false}>
			<TextField source='id' />
			<TextField source='text' label='內容' />
			<ArrayField source="aliases" label='別名' sortable={false}>
				<SingleFieldList>
					<ChipField source="text" />
				</SingleFieldList>
			</ArrayField>
			<ReferenceArrayField label="有效區域" source="regions" reference="regionAll" sortable={false}>
				<SingleFieldList>
					<ChipField source="name" />
				</SingleFieldList>
			</ReferenceArrayField>
			<ReferenceArrayField label="不全站搜尋" source="excludeSources" reference="source" sortable={false}>
				<SingleFieldList>
					<ChipField source="name" />
				</SingleFieldList>
			</ReferenceArrayField>
			<NumberField source='limit' label='搜尋量上限' />
			<NumberField source='targetCount' label='目標數量' />
			<NumberField source='targetIncrease' label='新增目標量' />
			<NumberField source='viewCount' label='累計瀏覽量' />
			<NumberField source='viewIncrease' label='瀏覽增加量' />
			<NumberField source='viewOfNew' label='新增目標瀏覽量' />
			<NumberField source='reactCount' label='累計反應量' />
			<NumberField source='reactIncrease' label='反應增加量' />
			<NumberField source='reactOfNew' label='新增目標反應量' />
			<NumberField source='commentCount' label='留言數量' />
			<NumberField source='commentIncrease' label='留言增加量' />
			<NumberField source='commentOfNew' label='新增目標留言量' />
			<KeywordSearchModeField source='searchMode' />
			<KeywordStatusField source='status' />
			<DateField source='createTime' label='建立時間' showTime />
			<ListEditButton edit={authInfo.keywordEdit} />
			<ShowButton />
		</Datagrid>
	</List>;
}

/** 關鍵字資訊頁面。 */
export const KeywordShow = (props: any) =>
{
	let { permissions } = usePermissions();
	let	authInfo = permissions as MemberAuthInfo;
	let opLogs : JSX.Element = <></>;
	let	statistics : JSX.Element = <></>;

	if(authInfo.group <= MEMBER_GROUP.Manager && authInfo.memberCreate)
	{
		opLogs = <Tab label='操作記錄'>
			<ReferenceManyField reference='memberLog' target='ml_int1' filter={{ ml_int1: props.id, 'type>=': MEMBER_LOG_TYPE.KeywordCreate, 'type<': MEMBER_LOG_TYPE.ManuallyChannelCreate }} pagination={<Pagination />}>
				<Datagrid bulkActionButtons={false}>
					<TextField source='id' />
					<ReferenceField label="操作者" source="memberId" reference="member">
						<TextField source="name" />
					</ReferenceField>
					<MemberLogTypeField label='執行動作' />
					<MemberLogDataField label='動作內容' />
					<DateField source='logTime' label='紀錄時間' showTime />
					<TextField source='remoteAddress' label='遠端位址' />
				</Datagrid>
			</ReferenceManyField>
		</Tab>;

		statistics = <>
			<NumberField source='viewCount' label='累計瀏覽量' />
			<NumberField source='viewIncrease' label='瀏覽增加量' />
		</>;
	}

	return <Show {...props}>
		<TabbedShowLayout>
			<Tab label='總覽'>
				<TextField source='id' />
				<TextField source='text' label='內容' />
				<ArrayField source="aliases" label='別名'>
					<SingleFieldList>
						<ChipField source="text" />
					</SingleFieldList>
				</ArrayField>
				<ReferenceArrayField label="有效區域" source="regions" reference="region">
					<SingleFieldList>
						<ChipField source="name" />
					</SingleFieldList>
				</ReferenceArrayField>
				<ReferenceArrayField label="不全站搜尋" source="excludeSources" reference="source" sortable={false}>
					<SingleFieldList>
						<ChipField source="name" />
					</SingleFieldList>
				</ReferenceArrayField>
				<NumberField source='limit' label='搜尋量上限' />
				{statistics}
				<KeywordSearchModeField source='searchMode' />
				<KeywordStatusField source='status' />
				<DateField source='createTime' label='建立時間' showTime />
			</Tab>
			{opLogs}
		</TabbedShowLayout>
	</Show>;
}

/** 關鍵字編輯頁面。 */
export const KeywordEdit = (props: any) => (
	<Edit {...props}>
		<SimpleForm toolbar={<SaveOnlyToolbar/>}>
			<Labeled label="ID"><TextField source='id' /></Labeled>
			<TextInput source='text' label="關鍵字文字" />
			<ArrayInput source="aliases" label='別名'>
				<SimpleFormIterator inline>
					<TextInput source='text' label='別名文字' />
				</SimpleFormIterator>
			</ArrayInput>
			<Labeled label="有效區域（留空表示可用在所有區域）">
				<ReferenceArrayInput source='regions' reference='regionAll' >
					<AutocompleteArrayInput optionText='name' />
				</ReferenceArrayInput>
			</Labeled>
			<Labeled label="不全站搜尋的來源">
				<ReferenceArrayInput source='excludeSources' reference='source' >
					<AutocompleteArrayInput optionText='name' />
				</ReferenceArrayInput>
			</Labeled>
			<NumberInput source='limit' label="搜尋量上限" />
			<SelectInput source='searchMode' choices={keywordSearchModeList} label="搜尋模式" />
			<SelectInput source='status' choices={keywordStatusList} label="狀態" />
			<Labeled label="建立時間"><DateField source='createTime' showTime /></Labeled>
		</SimpleForm>
	</Edit>
);

/** 關鍵字建立頁面。 */
export const KeywordCreate = (props: any) => (
	<Create {...props}>
		<SimpleForm>
			<TextInput source='text' label="關鍵字文字" />
			<ArrayInput source="aliases" label="別名">
				<SimpleFormIterator inline>
					<TextInput source='text' label='別名文字' />
				</SimpleFormIterator>
			</ArrayInput>
			<Labeled label="有效區域（留空表示可用在所有區域）">
				<ReferenceArrayInput source='regions' reference='regionAll' >
					<AutocompleteArrayInput optionText='name' />
				</ReferenceArrayInput>
			</Labeled>
			<Labeled label="不全站搜尋的來源">
				<ReferenceArrayInput source='excludeSources' reference='source' >
					<AutocompleteArrayInput optionText='name' />
				</ReferenceArrayInput>
			</Labeled>
			<NumberInput source='limit' label="搜尋量上限" />
			<SelectInput source='searchMode' choices={keywordSearchModeList} label="搜尋模式" />
			<SelectInput source='status' choices={keywordStatusList} label="狀態" />
		</SimpleForm>
	</Create>
);
